import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, LogOut, Download, Upload, Trash2, Check, X, Pencil } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ImageUpload from "../components/ImageUpload";

const inputCls =
  "w-full bg-white border border-[#181816]/15 px-4 py-3 text-sm font-light focus:border-[#181816] transition-colors";

const emptyProject = {
  title: "", category: "Residential", location: "", area: "", year: "", typology: "",
  description: "", story: "", cover_image: "", gallery: "", materials: "", featured: false, order: 0,
};

const toForm = (p) => ({
  ...p,
  gallery: (p.gallery || []).join("\n"),
  materials: (p.materials || []).join(", "),
});

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A857D]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState(null);

  const [projectForm, setProjectForm] = useState(null); // null = closed
  const [projectEditId, setProjectEditId] = useState(null);
  const [serviceForm, setServiceForm] = useState(null);
  const [serviceEditId, setServiceEditId] = useState(null);

  const refresh = async () => {
    try {
      const [s, p, sv, iq, st] = await Promise.all([
        api.get("/settings"), api.get("/projects"), api.get("/services"), api.get("/inquiries"), api.get("/stats"),
      ]);
      setSettings(s.data);
      setProjects(p.data);
      setServices(sv.data);
      setInquiries(iq.data);
      setStats(st.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin");
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    api.get("/auth/me").catch(() => navigate("/admin"));
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    ["projects", "Projects"], ["services", "Services"], ["inquiries", "Inquiries"], ["settings", "Settings"], ["backup", "Backup"],
  ];

  // ----- project actions -----
  const saveProject = async (e) => {
    e.preventDefault();
    const payload = {
      ...projectForm,
      gallery: projectForm.gallery.split("\n").map((s) => s.trim()).filter(Boolean),
      materials: projectForm.materials.split(",").map((s) => s.trim()).filter(Boolean),
      order: Number(projectForm.order) || 0,
    };
    try {
      if (projectEditId) await api.put(`/projects/${projectEditId}`, payload);
      else await api.post("/projects", payload);
      toast.success(projectEditId ? "Project updated" : "Project added");
      setProjectForm(null);
      setProjectEditId(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Delete “${p.title}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Project deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const galleryInputRef = useRef(null);
  const [galleryBusy, setGalleryBusy] = useState(false);

  const uploadGalleryFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setGalleryBusy(true);
    const urls = [];
    try {
      for (const f of files) {
        if (!f.type.startsWith("image/")) continue;
        if (f.size > 10 * 1024 * 1024) continue;
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd);
        urls.push(`/api/media/${r.data.path}`);
      }
      if (urls.length) {
        setProjectForm((pf) => ({ ...pf, gallery: [pf.gallery, ...urls].filter(Boolean).join("\n") }));
        toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGalleryBusy(false);
    }
  };

  // ----- service actions -----
  const saveService = async (e) => {
    e.preventDefault();
    try {
      if (serviceEditId) await api.put(`/services/${serviceEditId}`, serviceForm);
      else await api.post("/services", serviceForm);
      toast.success(serviceEditId ? "Service updated" : "Service added");
      setServiceForm(null);
      setServiceEditId(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteService = async (s) => {
    if (!window.confirm(`Delete “${s.title}”?`)) return;
    await api.delete(`/services/${s.id}`).catch((e) => toast.error(formatApiError(e)));
    toast.success("Service deleted");
    refresh();
  };

  // ----- inquiry actions -----
  const toggleInquiry = async (iq) => {
    await api.patch(`/inquiries/${iq.id}`, { responded: !iq.responded }).catch((e) => toast.error(formatApiError(e)));
    refresh();
  };

  const deleteInquiry = async (iq) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/inquiries/${iq.id}`).catch((e) => toast.error(formatApiError(e)));
    refresh();
  };

  // ----- settings -----
  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const r = await api.put("/settings", settings);
      setSettings(r.data);
      toast.success("Studio details saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ----- backup -----
  const downloadBackup = async () => {
    try {
      const r = await api.get("/backup");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `space-arts-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.post("/backup/restore", { ...data, confirm: true });
      toast.success("Backup restored");
      refresh();
    } catch (err) {
      toast.error("Invalid backup file");
    } finally {
      e.target.value = "";
    }
  };

  if (!user) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A857D]">Checking session…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen px-6 sm:px-10 py-10 bg-[#F1EFEA]">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <Link to="/" data-testid="admin-dashboard-home-link" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D] hover:text-[#181816] transition-colors">
              ← spaceartsbyak.com
            </Link>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl">Studio Dashboard</h1>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A857D]">{user.email}</p>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/admin"); }}
            data-testid="admin-logout-button"
            className="w-fit inline-flex items-center gap-2 border border-[#181816]/20 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#181816] hover:text-[#F8F6F0] transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
            {[["Projects", stats.projects], ["Featured", stats.featured], ["Inquiries", stats.inquiries], ["Unanswered", stats.unanswered]].map(([k, v]) => (
              <div key={k} className="bg-white/70 border hairline px-6 py-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A857D]">{k}</p>
                <p className="mt-1 font-serif text-3xl">{v}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-2 border-b hairline pb-px">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                tab === key ? "bg-[#181816] text-[#F8F6F0]" : "text-[#57534E] hover:text-[#181816]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {/* PROJECTS TAB */}
          {tab === "projects" && (
            <div data-testid="admin-tab-panel-projects">
              <div className="flex justify-between items-center">
                <h2 className="font-serif text-2xl">Projects ({projects.length})</h2>
                <button
                  onClick={() => { setProjectForm({ ...emptyProject }); setProjectEditId(null); }}
                  data-testid="admin-add-project-button"
                  className="inline-flex items-center gap-2 bg-[#181816] text-[#F8F6F0] px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#B85D3B] transition-colors"
                >
                  <Plus className="h-4 w-4" /> Add Project
                </button>
              </div>

              {projectForm && (
                <form onSubmit={saveProject} className="mt-6 bg-white/80 border hairline p-6 sm:p-8 grid sm:grid-cols-2 gap-5" data-testid="admin-project-form">
                  <Field label="Title *"><input required value={projectForm.title} onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })} className={inputCls} data-testid="admin-project-title-input" /></Field>
                  <Field label="Category">
                    <select value={projectForm.category} onChange={(e) => setProjectForm({ ...projectForm, category: e.target.value })} className={inputCls} data-testid="admin-project-category-select">
                      {["Residential", "Cultural", "Hospitality", "Interiors", "Commercial"].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Location"><input value={projectForm.location} onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })} className={inputCls} /></Field>
                  <Field label="Area"><input value={projectForm.area} onChange={(e) => setProjectForm({ ...projectForm, area: e.target.value })} placeholder="e.g. 240 m²" className={inputCls} /></Field>
                  <Field label="Year"><input value={projectForm.year} onChange={(e) => setProjectForm({ ...projectForm, year: e.target.value })} placeholder="e.g. 2025" className={inputCls} /></Field>
                  <Field label="Typology"><input value={projectForm.typology} onChange={(e) => setProjectForm({ ...projectForm, typology: e.target.value })} placeholder="e.g. Private Residence" className={inputCls} /></Field>
                  <div className="sm:col-span-2"><ImageUpload label="Cover Photo" value={projectForm.cover_image} onChange={(v) => setProjectForm({ ...projectForm, cover_image: v })} testid="admin-project-cover-upload" /></div>
                  <div className="sm:col-span-2"><Field label="Short Description"><textarea rows={2} value={projectForm.description} onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })} className={inputCls + " resize-none"} /></Field></div>
                  <div className="sm:col-span-2"><Field label="Full Story"><textarea rows={4} value={projectForm.story} onChange={(e) => setProjectForm({ ...projectForm, story: e.target.value })} className={inputCls + " resize-none"} /></Field></div>
                  <div className="sm:col-span-2">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <button
                        type="button"
                        onClick={() => galleryInputRef.current?.click()}
                        disabled={galleryBusy}
                        data-testid="admin-project-gallery-upload"
                        className="inline-flex items-center gap-2 border border-[#181816]/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] hover:border-[#181816] disabled:opacity-50 transition-colors"
                      >
                        <Upload className="h-3.5 w-3.5" /> {galleryBusy ? "Uploading…" : "Upload Gallery Photos"}
                      </button>
                      <span className="font-mono text-[10px] text-[#8A857D]">or paste URLs below (one per line)</span>
                      <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={uploadGalleryFiles} />
                    </div>
                    <Field label="Gallery Image URLs"><textarea rows={3} value={projectForm.gallery} onChange={(e) => setProjectForm({ ...projectForm, gallery: e.target.value })} className={inputCls + " resize-none"} /></Field>
                  </div>
                  <Field label="Materials (comma separated)"><input value={projectForm.materials} onChange={(e) => setProjectForm({ ...projectForm, materials: e.target.value })} placeholder="Travertine, Oak, Brass" className={inputCls} /></Field>
                  <Field label="Display order"><input type="number" value={projectForm.order} onChange={(e) => setProjectForm({ ...projectForm, order: e.target.value })} className={inputCls} /></Field>
                  <label className="flex items-center gap-3 font-light text-sm cursor-pointer">
                    <input type="checkbox" checked={projectForm.featured} onChange={(e) => setProjectForm({ ...projectForm, featured: e.target.checked })} data-testid="admin-project-featured-check" />
                    Feature on home page
                  </label>
                  <div className="sm:col-span-2 flex gap-3">
                    <button type="submit" data-testid="admin-project-save-button" className="bg-[#181816] text-[#F8F6F0] px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#B85D3B] transition-colors">Save Project</button>
                    <button type="button" onClick={() => { setProjectForm(null); setProjectEditId(null); }} className="border border-[#181816]/20 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-[#181816] transition-colors">Cancel</button>
                  </div>
                </form>
              )}

              <div className="mt-6 flex flex-col gap-3">
                {projects.map((p) => (
                  <div key={p.id} className="bg-white/70 border hairline px-5 py-4 flex items-center gap-4" data-testid={`admin-project-row-${p.slug}`}>
                    {p.cover_image ? <img src={p.cover_image} alt="" className="h-14 w-20 object-cover shrink-0" /> : <div className="h-14 w-20 bg-[#EFECE6] shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg truncate">{p.title} {p.featured && <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#C29B38] align-middle">Featured</span>}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A857D]">{p.category} · {p.location || "—"} · {p.year || "—"}</p>
                    </div>
                    <button onClick={() => { setProjectForm(toForm(p)); setProjectEditId(p.id); }} data-testid={`admin-project-edit-${p.slug}`} className="p-2.5 border border-[#181816]/15 hover:border-[#181816] transition-colors" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => deleteProject(p)} data-testid={`admin-project-delete-${p.slug}`} className="p-2.5 border border-[#181816]/15 hover:border-[#B85D3B] hover:text-[#B85D3B] transition-colors" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SERVICES TAB */}
          {tab === "services" && (
            <div data-testid="admin-tab-panel-services">
              <div className="flex justify-between items-center">
                <h2 className="font-serif text-2xl">Services ({services.length})</h2>
                <button
                  onClick={() => { setServiceForm({ title: "", description: "", order: services.length + 1 }); setServiceEditId(null); }}
                  data-testid="admin-add-service-button"
                  className="inline-flex items-center gap-2 bg-[#181816] text-[#F8F6F0] px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#B85D3B] transition-colors"
                >
                  <Plus className="h-4 w-4" /> Add Service
                </button>
              </div>

              {serviceForm && (
                <form onSubmit={saveService} className="mt-6 bg-white/80 border hairline p-6 sm:p-8 flex flex-col gap-5" data-testid="admin-service-form">
                  <Field label="Title *"><input required value={serviceForm.title} onChange={(e) => setServiceForm({ ...serviceForm, title: e.target.value })} className={inputCls} /></Field>
                  <Field label="Description"><textarea rows={3} value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} className={inputCls + " resize-none"} /></Field>
                  <Field label="Display order"><input type="number" value={serviceForm.order} onChange={(e) => setServiceForm({ ...serviceForm, order: e.target.value })} className={inputCls} /></Field>
                  <div className="flex gap-3">
                    <button type="submit" className="bg-[#181816] text-[#F8F6F0] px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#B85D3B] transition-colors">Save Service</button>
                    <button type="button" onClick={() => setServiceForm(null)} className="border border-[#181816]/20 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-[#181816] transition-colors">Cancel</button>
                  </div>
                </form>
              )}

              <div className="mt-6 flex flex-col gap-3">
                {services.map((s) => (
                  <div key={s.id} className="bg-white/70 border hairline px-5 py-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg">{s.title}</p>
                      <p className="text-sm font-light text-[#8A857D] line-clamp-1">{s.description}</p>
                    </div>
                    <button onClick={() => { setServiceForm({ ...s }); setServiceEditId(s.id); }} className="p-2.5 border border-[#181816]/15 hover:border-[#181816] transition-colors" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => deleteService(s)} className="p-2.5 border border-[#181816]/15 hover:border-[#B85D3B] hover:text-[#B85D3B] transition-colors" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* INQUIRIES TAB */}
          {tab === "inquiries" && (
            <div data-testid="admin-tab-panel-inquiries">
              <h2 className="font-serif text-2xl">Client Inquiries ({inquiries.length})</h2>
              <div className="mt-6 flex flex-col gap-3">
                {inquiries.map((iq) => (
                  <div key={iq.id} className={`bg-white/70 border hairline px-5 py-4 ${iq.responded ? "opacity-60" : ""}`} data-testid={`admin-inquiry-${iq.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-serif text-lg">{iq.name} <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A857D] ml-2">{new Date(iq.created_at).toLocaleDateString()}</span></p>
                        <p className="text-sm font-light text-[#57534E]">{iq.email}{iq.phone ? ` · ${iq.phone}` : ""}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A857D]">{iq.project_type} · {iq.budget}</p>
                        <p className="mt-3 font-light text-[#181816]">{iq.message}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => toggleInquiry(iq)}
                          data-testid={`admin-inquiry-respond-${iq.id}`}
                          className={`inline-flex items-center gap-2 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] border transition-colors ${iq.responded ? "border-[#5E6554] text-[#5E6554]" : "border-[#181816]/20 hover:border-[#181816]"}`}
                        >
                          {iq.responded ? <><X className="h-3.5 w-3.5" /> Responded</> : <><Check className="h-3.5 w-3.5" /> Mark responded</>}
                        </button>
                        <button onClick={() => deleteInquiry(iq)} className="p-2.5 border border-[#181816]/15 hover:border-[#B85D3B] hover:text-[#B85D3B] transition-colors" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
                {inquiries.length === 0 && <p className="font-light text-[#8A857D]">No inquiries yet. New website inquiries will land here.</p>}
              </div>
            </div>
          )}

          {/* SETTINGS TAB */}
          {tab === "settings" && settings && (
            <div data-testid="admin-tab-panel-settings">
              <h2 className="font-serif text-2xl">Studio Details & Social Links</h2>
              <form onSubmit={saveSettings} className="mt-6 bg-white/80 border hairline p-6 sm:p-8 grid sm:grid-cols-2 gap-5">
                <Field label="Email"><input value={settings.email || ""} onChange={(e) => setSettings({ ...settings, email: e.target.value })} className={inputCls} data-testid="admin-settings-email" /></Field>
                <Field label="Phone"><input value={settings.phone || ""} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} className={inputCls} data-testid="admin-settings-phone" /></Field>
                <div className="sm:col-span-2"><Field label="Studio Address"><input value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} className={inputCls} /></Field></div>
                <div className="sm:col-span-2"><ImageUpload label="Principal Photo (shown on Home & About)" value={settings.principal_photo || ""} onChange={(v) => setSettings({ ...settings, principal_photo: v })} testid="admin-settings-principal-upload" /></div>
                {[["instagram", "Instagram URL"], ["linkedin", "LinkedIn URL"], ["pinterest", "Pinterest URL"], ["facebook", "Facebook URL"], ["youtube", "YouTube URL"], ["whatsapp", "WhatsApp URL (wa.me/…)"]].map(([key, label]) => (
                  <Field key={key} label={label}>
                    <input value={settings[key] || ""} onChange={(e) => setSettings({ ...settings, [key]: e.target.value })} placeholder="https://…" className={inputCls} data-testid={`admin-settings-${key}`} />
                  </Field>
                ))}
                <div className="sm:col-span-2">
                  <button type="submit" data-testid="admin-settings-save" className="bg-[#181816] text-[#F8F6F0] px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#B85D3B] transition-colors">Save Details</button>
                </div>
              </form>
            </div>
          )}

          {/* BACKUP TAB */}
          {tab === "backup" && (
            <div data-testid="admin-tab-panel-backup">
              <h2 className="font-serif text-2xl">Website Backup</h2>
              <p className="mt-3 max-w-xl font-light text-[#57534E] leading-relaxed">
                Download a complete copy of your website content — projects, services, inquiries and studio details — as a single JSON file. Restore it any time to roll the site back.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  onClick={downloadBackup}
                  data-testid="admin-backup-download"
                  className="inline-flex items-center gap-2 bg-[#181816] text-[#F8F6F0] px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#B85D3B] transition-colors"
                >
                  <Download className="h-4 w-4" /> Download Backup
                </button>
                <label className="inline-flex items-center gap-2 border border-[#181816]/20 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] cursor-pointer hover:border-[#181816] transition-colors">
                  <Upload className="h-4 w-4" /> Restore from File
                  <input type="file" accept="application/json" onChange={restoreBackup} className="hidden" data-testid="admin-backup-restore-input" />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
